import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { colors } from '../theme';

const VFDPanels: React.FC = () => {
  const typicalPanels = [
    'Redundant Bypass Fan Wall Panels',
    'Duplex and Triplex Lift Stations',
    'Cooling Tower Panels',
    'VFDs with Across The Line Bypass',
    'NEMA 3R Irrigation Panels',
    'VFDs with Built-in Harmonic Filters',
    'NEMA 4X Outdoor Compressor Panels',
  ];

  const industries = [
    'Water & Wastewater',
    'HVAC',
    'Oil & Gas',
    'Food & Beverage',
    'Chemical Processing',
    'Manufacturing',
  ];

  const applications = [
    'Pumps',
    'Fans',
    'Compressors',
    'Conveyors',
    'Mixers',
    'Extruders',
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label="Products"
              component={Link}
              to="/products"
              clickable
              sx={{
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              VFD Panels
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              Variable Frequency Drive Control Panels built to your exact specifications
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)` }} />

      {/* Main Content Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Left Column - Image */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/variable-frequency-drive-panels.jpg"
                  alt="VFD Panels"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>

            {/* Right Column - Content */}
            <Grid item xs={12} md={7}>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 4,
                  fontSize: '1.05rem',
                }}
              >
                Variable Frequency Drive (VFD) Control Panels are designed to withstand voltage surges, changing climate conditions, and job-specific space limitations.
              </Typography>

              {/* What Are VFDs? Breakout Box */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  border: 'none',
                  background: colors.background,
                  position: 'relative',
                  overflow: 'visible',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                    borderRadius: '4px 0 0 4px',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.secondary,
                      mb: 2,
                      fontWeight: 700,
                    }}
                  >
                    What Are VFDs?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textSecondary,
                      lineHeight: 1.8,
                    }}
                  >
                    VFDs are electronic devices that control the speed of AC motors by varying the frequency and voltage of the power supplied to the motor. They are also known as adjustable frequency drives (AFDs), variable speed drives (VSDs), or inverters.
                  </Typography>
                </CardContent>
              </Card>

              {/* VFD Build and Installation */}
              <Typography
                variant="h6"
                sx={{
                  color: colors.secondary,
                  mb: 2,
                  fontWeight: 700,
                }}
              >
                VFD Build and Installation
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                Our team of engineers and technicians have years of experience in building and installing VFD panels. We work closely with our customers to ensure that their panels are built to their exact specifications and installed correctly.
              </Typography>

              {/* Retrofit Services */}
              <Typography
                variant="h6"
                sx={{
                  color: colors.secondary,
                  mb: 2,
                  fontWeight: 700,
                }}
              >
                Retrofit Services
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.8,
                  mb: 4,
                }}
              >
                We offer retrofit services for existing VFD panels. Our team can upgrade your existing panels with new components, or replace your old panels with new ones.
              </Typography>

              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Contact Us
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Typical Panels, Industries, Applications */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Typical VFD Panels */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: colors.border,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    Typical VFD Panels We Build
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {typicalPanels.map((panel, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: colors.primary,
                            mt: '0.5em',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                          {panel}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Industries Served */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: colors.border,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    Industries Served
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {industries.map((industry, index) => (
                      <Chip
                        key={index}
                        label={industry}
                        sx={{
                          backgroundColor: 'rgba(245, 166, 35, 0.1)',
                          color: colors.secondary,
                          fontWeight: 500,
                          border: `1px solid ${colors.primary}`,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Common Applications */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: colors.border,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    Common Applications
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {applications.map((app, index) => (
                      <Chip
                        key={index}
                        label={app}
                        sx={{
                          backgroundColor: 'rgba(45, 45, 45, 0.1)',
                          color: colors.secondary,
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Back to Products */}
      <Box
        sx={{
          py: 4,
          background: '#FFFFFF',
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <Button
          component={Link}
          to="/products"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
        >
          Back to All Products
        </Button>
      </Box>
    </Box>
  );
};

export default VFDPanels;
